/**
 * Utility functions for Founder Dashboard
 */

/** Format Date to YYYY-MM-DD in local timezone */
function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Calculate percent change between current and previous value.
 * Returns null if previous is 0 or not finite.
 */
export function percentChange(
  current: number,
  previous: number
): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  const diff = current - previous;
  return (diff / previous) * 100;
}

/** Normalize to YYYY-MM-DD for consistent map keys (API may send "2026-01-20" or "2026-01-20T00:00:00.000Z") */
function toDateKey(date: string): string {
  if (!date) return '';
  const part = String(date).split('T')[0];
  return part || String(date).slice(0, 10);
}

/**
 * Bucket dates in range into daily slots, filling gaps with zeros.
 */
export function fillMissingDatesInRange<T extends { date: string }>(
  items: T[],
  startDate: string,
  endDate: string,
  defaultFn: (date: string) => T
): T[] {
  const byDate = new Map<string, T>();
  items.forEach(item => byDate.set(toDateKey(item.date), item));

  const result: T[] = [];
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T23:59:59');
  const oneDay = 24 * 60 * 60 * 1000;
  let d = new Date(start.getTime());

  while (d <= end) {
    const key = toLocalDateString(d);
    const resolved =
      byDate.get(key) ?? byDate.get(toDateKey(key)) ?? defaultFn(key);
    result.push(resolved);
    d = new Date(d.getTime() + oneDay);
  }
  return result;
}

/**
 * Get date range for previous period (same length as current range).
 */
export function getPreviousPeriodRange(
  startDate: string,
  endDate: string
): { startDate: string; endDate: string } {
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T23:59:59');
  const oneDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.max(
    0,
    Math.floor((end.getTime() - start.getTime()) / oneDay)
  );

  const prevEnd = new Date(start.getTime());
  prevEnd.setDate(prevEnd.getDate() - 1);
  prevEnd.setHours(23, 59, 59, 999);

  const prevStart = new Date(prevEnd.getTime());
  prevStart.setDate(prevStart.getDate() - diffDays);
  prevStart.setHours(0, 0, 0, 0);

  return {
    startDate: toLocalDateString(prevStart),
    endDate: toLocalDateString(prevEnd),
  };
}
